import { priorityCodeToId, statusCodeToId } from '@/lib/collection-case-codes';
import type {
  AssignCollectionCaseRequest,
  CollectionCaseListItemApi,
  CreateCollectionCaseRequest,
  UpdateCollectionCaseRequest,
} from '@/hooks/useCollectionCasesData';

/**
 * Demo-mode fabricated response for updating a collection case.
 * Used as the demoFn for useUpdateCollectionCase.
 */
export function updateCollectionCaseDemoResponse(request: UpdateCollectionCaseRequest): CollectionCaseListItemApi {
  return {
    caseId: String(request.caseId),
    caseNumber: `CC-MOCK`,
    customerId: 0,
    companyName: null,
    customerName: 'Mock Customer',
    customerAccountNumber: null,
    priority: Object.entries(priorityCodeToId).find(([, v]) => v === request.priorityId)?.[0] ?? 'Medium',
    status: Object.entries(statusCodeToId).find(([, v]) => v === request.statusId)?.[0] ?? 'Active',
    strategyId: request.strategyId ?? null,
    strategyName: null,
    amount: request.amount,
    daysInCollection: 0,
    nextAction: request.nextAction ?? null,
    nextActionDate: request.nextActionDate ?? null,
    riskLevel: null,
    assignedTo: null,
    assignedToName: null,
    contactId: request.contactId ?? null,
    notes: request.notes ?? null,
    currencyCode: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    activityCount: 0,
    transactionCount: 0,
    customerEmail: null,
    customerPhone: null,
    lastActionType: null,
    lastActionDescription: null,
    lastActionActorName: null,
    lastActionDate: null,
    currentStage: 0,
    totalStages: 1,
  };
}

/**
 * Demo-mode fabricated response for assigning a collection case.
 * Used as the demoFn for useAssignCollectionCase.
 */
export function assignCollectionCaseDemoResponse(request: AssignCollectionCaseRequest): CollectionCaseListItemApi {
  return {
    caseId: String(request.caseId),
    caseNumber: 'CC-MOCK',
    customerId: 0,
    companyName: null,
    customerName: 'Mock Customer',
    customerAccountNumber: null,
    priority: 'Medium',
    status: 'Active',
    strategyId: null,
    strategyName: null,
    amount: 0,
    daysInCollection: 0,
    nextAction: null,
    nextActionDate: null,
    riskLevel: null,
    assignedTo: request.assignedToUserId,
    assignedToName: 'Assigned User',
    contactId: null,
    notes: null,
    currencyCode: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    activityCount: 0,
    transactionCount: 0,
    customerEmail: null,
    customerPhone: null,
    lastActionType: null,
    lastActionDescription: null,
    lastActionActorName: null,
    lastActionDate: null,
    currentStage: 0,
    totalStages: 1,
  };
}

/**
 * Demo-mode fabricated response for creating a collection case.
 * Used as the demoFn for useCreateCollectionCase.
 */
export function createCollectionCaseDemoResponse(request: CreateCollectionCaseRequest): CollectionCaseListItemApi {
  return {
    caseId: String(Date.now()),
    caseNumber: `CC-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-MOCK`,
    customerId: request.customerId,
    companyName: null,
    customerName: 'Mock Customer',
    customerAccountNumber: null,
    priority: request.priorityId === 1 ? 'High' : request.priorityId === 3 ? 'Low' : 'Medium',
    status: 'Active',
    strategyId: request.strategyId ?? null,
    strategyName: null,
    amount: request.amount,
    daysInCollection: 0,
    nextAction: request.nextAction ?? null,
    nextActionDate: request.nextActionDate ?? null,
    riskLevel: null,
    assignedTo: request.assignedToUserId,
    assignedToName: 'Assigned User',
    contactId: request.contactId ?? null,
    notes: request.notes ?? null,
    createdAt: new Date().toISOString(),
    updatedAt: null,
    activityCount: 0,
    transactionCount: 0,
    customerEmail: null,
    customerPhone: null,
    lastActionType: null,
    lastActionDescription: null,
    lastActionActorName: null,
    lastActionDate: null,
    currentStage: 0,
    totalStages: 1,
    currencyCode: request.currencyCode ?? null,
  };
}
